import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  Link,
  Tabs,
  Tab,
} from '@material-ui/core'
import { ShoppingBag as ShoppingBagIcon } from 'react-feather'
import { useHistory } from 'react-router-dom'

import useSubscription from 'hooks/useSubscription'
import useSnackbar from 'hooks/useSnackbar'

import { Page, Container, LoadingFeedback } from 'components'
import {
  BilletStep,
  FormCreditCard,
  PixStep,
} from 'components/PreRegistration/Payment/components'
import { PaymentFeedbackScreen, ResumeCard } from 'components/PreRegistration'

import constants from 'constants/index'

import * as service from 'service'

import helpers from 'helpers'
import theme from 'theme'

import useStyles from './styles'

const PaymentLink = ({
  match: {
    params: { paymentLinkToken },
  },
}) => {
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: true,
  })

  const snackbar = useSnackbar()
  const {
    isLoading,
    setIsLoading,
    setStatus,
    status,
    setPaymentData,
    paymentData,
  } = useSubscription()
  const [data, setData] = useState(false)
  const [currentTab, setCurrentTab] = useState('card')
  const history = useHistory()

  const classes = useStyles()

  const handleGenerate = () => {
    if (currentTab === 'card') return null

    submitPayment({})
  }

  const submitPayment = async (data) => {
    try {
      setIsLoading(true)
      const brand = constants.cardFlag.CARD_BRAND_DEFINITION_BY_ID[data.method]
      const method =
        currentTab === 'billet' ? 6 : currentTab === 'pix' ? 7 : data.method
      const response = await service.dponet.paymentLink.create({
        paymentLinkToken,
        paymentLink: {
          method: method,
          ...(currentTab === 'card' && {
            card: {
              ...data.card,
              brand,
            },
          }),
        },
      })

      if (response.status === 207) {
        return setStatus(constants.preRegistrations.INTERNAL_ERROR_STATUS)
      }

      setStatus(
        response?.data?.paymentOrder?.status ??
          constants.preRegistrations.FAILURE_STATUS,
      )

      setPaymentData(response?.data?.paymentOrder)
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error.response.data.error),
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const showCheckout = async () => {
      try {
        setIsLoading(true)
        const response = await service.dponet.paymentLink.show(paymentLinkToken)

        setData(response?.data?.paymentOrder ?? {})
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error.response.data.error),
          variant: 'error',
        })

        history.push('/')
      } finally {
        setIsLoading(false)
      }
    }

    showCheckout()
    //eslint-disable-next-line
  }, [])

  if (!!status) {
    const method = paymentData?.paymentMethod?.method
    if (method === constants.preRegistrations.BILLET_METHOD)
      return <BilletStep />
    if (method === constants.preRegistrations.PIX_METHOD) return <PixStep />
    return <PaymentFeedbackScreen enableTreat={false} />
  }

  const handleTabsChange = (_, value) => setCurrentTab(value)

  const address = data?.buyer?.address

  return (
    <Page title="Link de pagamento">
      <LoadingFeedback open={isLoading} />
      <Box display="flex" justifyContent="center" my={2}>
        <Container maxWidth={false}>
          <Box mb={2}>
            <Box mb={6}>
              <Typography variant="h2" gutterBottom>
                Link de pagamento
              </Typography>
            </Box>
            <Typography variant="h4" className={classes.subtitle}>
              Você está prestes a efetuar o pagamento de uma mensalidade
              referente a utilização de um serviço da DPOnet.
            </Typography>
            <Box mt={2}>
              <Typography variant="h4" className={classes.subtitle}>
                Por favor, verifique os dados abaixo e escolha a forma de
                pagamento.
              </Typography>
            </Box>
          </Box>
          <Grid
            container
            spacing={2}
            direction={smallScreen ? 'column-reverse' : 'row'}
          >
            <Grid item xs={12} md={7} lg={8} xl={9}>
              <Grid item xs={12}>
                <Paper component={Box} pb={2}>
                  <Tabs
                    onChange={handleTabsChange}
                    value={currentTab}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    aria-label="Forma de pagamento"
                  >
                    <Tab label="Cartão de crédito" value="card" />
                    <Tab label="Boleto bancário" value="billet" />
                    <Tab label="Pix" value="pix" />
                  </Tabs>
                </Paper>
              </Grid>
              <Paper component={Box} p={2}>
                {currentTab === 'card' && (
                  <>
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        Após o pagamento, o prazo de compensação é imediato.
                        Assim que o pagamento for confirmado, você receberá um
                        e-mail com a confirmação da mensalidade.
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        O vencimento do link de pagamento está definido para{' '}
                        <strong>
                          {helpers.formatters.date(
                            data?.expirationDate,
                            'DD/MM/YYYY',
                          )}{' '}
                          às 11:59
                        </strong>
                        . Após essa data, o link de pagamento não será mais
                        válido e você precisará gerar um novo link de pagamento.
                      </Typography>
                    </Box>
                    <FormCreditCard
                      submitPayment={submitPayment}
                      formSubmitId="payment-link-submit"
                    />
                  </>
                )}
                {currentTab === 'billet' && (
                  <Box>
                    <Typography variant="body1" gutterBottom>
                      Será gerado um boleto bancário para o pagamento da
                      mensalidade. Você poderá pagá-lo em qualquer banco ou
                      aplicativo de pagamento.
                      <br />
                      <br />
                      Após o pagamento, o prazo de compensação é de até 3 dias
                      úteis. Assim que o pagamento for confirmado, você receberá
                      um e-mail com a confirmação da mensalidade.
                    </Typography>
                    <br />
                    <br />
                    <Typography variant="body1" gutterBottom>
                      O vencimento do boleto esta definido para{' '}
                      <strong>
                        {helpers.formatters.date(
                          data?.expirationDate,
                          'DD/MM/YYYY',
                        )}{' '}
                        às 11:59
                      </strong>
                      . Após essa data, o link de pagamento não será mais válido
                      e você precisará gerar um novo link de pagamento.
                    </Typography>
                  </Box>
                )}
                {currentTab === 'pix' && (
                  <Box>
                    <Typography variant="body1" gutterBottom>
                      Será gerado um QR Code e um código Pix para o pagamento da
                      mensalidade. Você poderá pagá-lo através do seu banco ou
                      aplicativo de pagamento.
                      <br />
                      <br />
                      Após o pagamento, o prazo de compensação é imediato. Assim
                      que o pagamento for confirmado, você receberá um e-mail
                      com a confirmação da mensalidade.
                    </Typography>
                    <br />
                    <br />
                    <Typography variant="body1" gutterBottom>
                      O vencimento do pix esta definido para{' '}
                      <strong>
                        {helpers.formatters.date(
                          data?.expirationDate,
                          'DD/MM/YYYY',
                        )}{' '}
                        às 11:59
                      </strong>
                      . Após essa data, o link de pagamento não será mais válido
                      e você precisará gerar um novo link de pagamento.
                    </Typography>
                  </Box>
                )}
              </Paper>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexDirection={smallScreen ? 'column' : 'row'}
                mt={2}
              >
                <Box mt={2}>
                  <Button
                    variant="contained"
                    type={currentTab === 'card' ? 'submit' : 'button'}
                    form="payment-link-submit"
                    color="primary"
                    onClick={handleGenerate}
                  >
                    {currentTab === 'card'
                      ? 'Efetuar pagamento'
                      : currentTab === 'billet'
                      ? 'Gerar boleto'
                      : 'Gerar QR Code'}
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} lg={4} xl={3}>
              <ResumeCard icon={ShoppingBagIcon} title="RESUMO" defaultExpanded>
                <Box p={2}>
                  <Box>
                    <Typography variant="body2">Faturar para</Typography>
                    <Typography variant="subtitle1">
                      {`${data?.buyer?.name} - ${
                        data?.buyer?.documentType
                      }: ${helpers.formatters.document.identify(
                        data?.buyer?.document,
                      )}`}
                    </Typography>
                  </Box>
                  <Box my={2}>
                    <Typography variant="body2">Endereço</Typography>
                    <Typography variant="subtitle1">
                      {`${address?.street}, ${address?.number}`},{' '}
                      {address?.neighborhood}, {address?.city} -{' '}
                      {address?.state},{' '}
                      {helpers.formatters.cep(address?.postalCode)}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2">
                      Referência da cobrança
                    </Typography>
                    <Typography variant="subtitle1">
                      {helpers.formatters.date(
                        data?.orderReferenceDate,
                        'MMMM',
                      ) +
                        ' de ' +
                        helpers.formatters.date(
                          data?.orderReferenceDate,
                          'YYYY',
                        )}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2">Número do contrato</Typography>
                    <Typography variant="subtitle1">
                      {data?.contractIdentifier ?? '-'}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2">
                      Pagamento processado por
                    </Typography>
                    <Typography variant="subtitle1">
                      Superfin Intermediadora LTDA – CNPJ: 49.777.920/0001-00
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2">Pagamento para</Typography>
                    <Typography variant="subtitle1">
                      DPOnet Desenvolvimento de Sistemas e Consultoria em
                      Segurança da Informação Ltda – CNPJ: 36.487.128/0001-79
                    </Typography>
                  </Box>
                  {data?.dataNfe?.nNfse && (
                    <Box>
                      <Typography variant="body2">Número da nota</Typography>
                      <Link
                        variant="subtitle1"
                        className={classes.link}
                        href="#"
                        onClick={() =>
                          window.open(
                            data?.dataNfe?.cUrlNfse,
                            '_blank',
                            'noopener noreferrer',
                          )
                        }
                      >
                        {data?.dataNfe?.nNfse ?? '-'}
                      </Link>
                    </Box>
                  )}
                </Box>
                <Divider />
                <Box ml={2.8} my={2} className={classes.gridStart}>
                  <Typography variant="h6">Total a pagar</Typography>
                  <Typography variant="h5" className={classes.price}>
                    R$ {helpers.formatters.currencyInput(data?.price)}
                  </Typography>
                </Box>
              </ResumeCard>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Page>
  )
}

export default PaymentLink
